/**
 * PLEASE READ BELOW!
 *
 * Please commit to this file if:
 * - The bug you're trying to fix can't be fixed by a more generic CSS style applied to the CSS of `MessageHtml`
 * - Somehow the email has shady CSS that even Gmail doesn't render perfectly.
 * - A server-side fix would be too complex/long to implement
 *
 * If you don't have any other choices than to add a new hack there, please follow some simple rules:
 *
 * - Document it as thoroughly as possible. The more explications/background the better.
 * - Please please please add the original selector, we're targeting obfuscated classes here so without the original classnames, it's near impossible to trace the original CSS selector
 * - Make your CSS selector as specific as possible, the last thing we want is to have side-effects on that kind of code.
 */

export const emailCssHacks = css`
  /**
   * Emails sent/generated by http://sap.com seem to have a content entirely with "position: absolute", making it non-possible to make it so it flows properly inside P3.
   * A fix for that is to apply "position: relative" on the first element of the tree of that email so it stays properly in the flow.
   * 
   * The original selector is:
   * .ZCONTRACTSTYLE > #sf--PAGE1-001.page
   */
  .fa353ni7 > #sf--PAGE1-001.fa5k6xb4 {
    position: relative !important;
  }

  /* Hacks backported from Old Front */
  /* MsoListParagraph. Word/Outlook applies a text-indent that messes up the composer/message viewer. */
  .fa5xl8wa {
    text-indent: 0 !important;
  }

  /* p.MsoNormal */
  p.fab6k8g {
    margin: 0 !important;
    text-indent: 0 !important;
  }

  /* MsoNormalTable. Word/Outlook applies a margin-left that messes up the layout */
  table.fa-6kext0 {
    margin-left: 0 !important;
  }

  /* Workaround for absolutely positioned elements. */
  div[style*='position'][style*='absolute'] {
    /* Override position: absolute with position: relative so that the element takes up space. */
    position: relative !important;
  }
  div[style*='position'] div[style*='position'][style*='absolute'] {
    /* Restore position: absolute inside ancestors that are already positioned. */
    position: absolute !important;
  }

  /**
   * PB-42001: In Chrome (v109 at time of writing), strikethrough due to an
   * '<s>' tag that is inside of a heading is, in some cases, rendered as
   * underline. Given that underline and strikethrough may have substantially
   * different meaning in documents like contracts, this can be rather
   * problematic for customers.
   *
   * ## Notes ##
   * ###########
   * - https://codesandbox.io/s/strikethough-inside-heading-jl0ctw?file=/index.html
   * - This issue only affects Chrome, so the iOS app is not affected.
   * - This issue only affects strikethrough due to '<s>' and not strikethrough due to
   *   'text-decoration: line-through'.
   * - The offending HTML was generated by Outlook.
   */
  h1:has(s),
  h2:has(s),
  h3:has(s),
  h4:has(s),
  h5:has(s),
  h6:has(s) {
    /*
     * This fixes the issue. Unfortunately, as a side-effect, this fix may also
     * change the font size. However, in practice, the offending HTML that
     * raised this issue was generated by Outlook, where font sizes were
     * explicitly set on styles in nested spans, so this fix had no effect on
     * font size.
     */
    font-size: unset;
  }

  /**
   * PB-48778. It seems emails made by Pipefy have "overflow-y: hidden" on their "article.content" element. A server-side fix wouldn't be very practical so we're fixing it here.
   */
  article.fa-ca79z:has(blockquote#pipefy_quote) {
    overflow-y: visible;
  }
`;

/** This is a barebones version of the `css` helper from `styled-components` re-implemented to get the Nice™️ tooling in VScode
 * but without importing the actual `styled-components` which would not allow us to share this file between mobile and Web. */
function css(...strings: Array<TemplateStringsArray>) {
  return strings;
}
